





















import { Vue, Component, Prop } from "vue-property-decorator";
import { AxiosError } from "axios";
import _ from "@/boot/lodash";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ImportStepMap, ImportStepCodes } from "@/data/enums/imports";

// Types
import { IImport, IImportStep, IImportSourceError } from "@/models/imports";
import { IFile } from "@/models/files";

@Component({
  components: {
    "select-import-files-listing": () =>
      import("@/components/app/admin/imports/selectImportFilesListing.vue")
  }
})
export default class AttachFilesModal extends Vue {
  // Props

  @Prop({ required: true })
  public importId!: IImport;

  @Prop({ required: true })
  public stepId!: IImportStep["id"];

  @Prop({ default: () => [] })
  public selectedFileIds!: IFile["id"][];

  @Prop({ required: true })
  public stepType!: ImportStepCodes;

  // Data

  public isSyncing = false;
  public newSelectedFiles: IFile[] = [];
  public sourceFilesErrors: IImportSourceError[] = [];

  // Life-cycle methods

  public created() {
    this.newSelectedFiles = _.map(this.selectedFileIds, selectedFileId => {
      return {
        id: selectedFileId,
        name: ""
      } as IFile;
    });
  }

  // Methods

  public selectFile(selectedFile, value) {
    if (!value) {
      this.newSelectedFiles = _.filter(
        this.newSelectedFiles,
        newSelectedFile => newSelectedFile.id !== selectedFile.id
      );
    } else if (
      !_.some(
        this.newSelectedFileIds,
        newSelectedFileId => newSelectedFileId === selectedFile.id
      )
    ) {
      this.newSelectedFiles.push(selectedFile);
    }

    this.makeInactive(selectedFile.id);
  }

  public makeInactive(fileId) {
    const sourceFilesErrorIndex = _.findIndex(this.sourceFilesErrors, [
      "id",
      fileId
    ]);

    if (sourceFilesErrorIndex === -1) return;
    this.sourceFilesErrors[sourceFilesErrorIndex].isActive = false;
  }

  public async syncFiles() {
    try {
      this.isSyncing = true;
      await this.syncImportStepFiles();

      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.import_step_files_successfully_updated")
      });

      this.$emit("success");
      this.$emit("close");
    } catch (e) {
      const error = e as AxiosError;

      const errorMessages = error.response?.data?.error?.data || [];

      _.forEach(errorMessages, (errorValues, errorKey) => {
        const fileKeys = errorKey.replace("file_ids.", "").split(".");
        const file = _.find(this.newSelectedFiles, [
          "id",
          this.newSelectedFileIds[fileKeys[0]]
        ]);
        const sourceFilesErrorIndex = file
          ? _.findIndex(this.sourceFilesErrors, ["id", file.id])
          : -1;

        if (sourceFilesErrorIndex === -1) {
          this.sourceFilesErrors.push({
            id: file?.id || "",
            fileName: file?.original_name || "",
            isActive: true,
            errorMessages: [
              {
                fieldName: fileKeys[1],
                message: errorValues[0]
              }
            ]
          });
        } else {
          const errorMessageId = file
            ? _.findIndex(
                this.sourceFilesErrors[sourceFilesErrorIndex].errorMessages,
                ["fieldName", fileKeys[1]]
              )
            : -1;

          if (errorMessageId === -1) {
            this.sourceFilesErrors[sourceFilesErrorIndex].errorMessages.push({
              fieldName: fileKeys[1],
              message: errorValues[0]
            });
          } else {
            this.sourceFilesErrors[sourceFilesErrorIndex].errorMessages[
              errorMessageId
            ].message = errorValues[0];
          }

          this.sourceFilesErrors[sourceFilesErrorIndex].isActive = true;
        }
      });

      if (!_.isEmpty(errorMessages)) {
        this.$_.set(
          error,
          "response.data.error.message",
          this.$t("_sentence.imports.wrong_source_file_structure")
        );

        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      }
    } finally {
      this.isSyncing = false;
    }
  }

  public syncImportStepFiles() {
    return this.$store.dispatch(
      `data/${DataModules.IMPORTS_FILES}/syncImportStepImages`,
      {
        stepId: this.stepId,
        data: { file_ids: this.newSelectedFileIds }
      }
    );
  }

  // Computed

  public get isChanged() {
    return !_.isEqual(
      this.selectedFileIds.sort(),
      this.newSelectedFileIds.sort()
    );
  }

  public get newSelectedFileIds() {
    return _.map(this.newSelectedFiles, newSelectedFile => newSelectedFile.id);
  }

  public get title() {
    return `${ImportStepMap()[this.stepType].label} - ${this.$t(
      "_action.attach_csv_source_files"
    )}`;
  }
}
